import {AttachmentsUtil, formatBytes} from '@shared/utils/attachments-utils';
import {ReportingService, SelectedAccount} from '../../reporting.service';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, EventEmitter, Output} from '@angular/core';
import {RouterLink} from '@angular/router';
import {combineLatest, map, Observable, switchMap, take} from 'rxjs';
import {filterNulls, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {DatePipe, LoadComponent} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {
    ReportConsumersUploadService,
    ReportingAccountWithAttachments,
} from '@reporting/consumers/consumer-data-upload/report-consumers-upload.service';
import {AttachmentFile} from '../../costs/models/file-attachment.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ApproveUploadedConsumerDataModalComponent} from './approve-uploaded-consumer-data-modal/approve-uploaded-consumer-data-modal.component';

@Component({
    selector: 'app-consumer-data-upload',
    templateUrl: './consumer-data-upload.component.html',
    styleUrls: ['./consumer-data-upload.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterLink, TranslatePipe, LoadComponent, DatePipe],
})
export class ConsumerDataUploadComponent {
    @Output() gotoManualReporting = new EventEmitter<void>();
    public formatBytes = formatBytes;

    readonly view$: Observable<{
        periodId: string;
        selectedAccount: SelectedAccount;
        accounts: ReportingAccountWithAttachments[];
        attachmentsExistsOnAnAccount: boolean;
    }> = this.reportingService.selectedAccount$.pipe(
        takeUntilDestroyed(),
        filterNulls(),
        switchMap(({account, building}) =>
            combineLatest({
                beginReporting: this.reportConsumersUploadService.beginConsumerReporting(
                    building.buildingNo,
                    building.periodId,
                ),
                accounts: this.reportConsumersUploadService.fetchAccountAttachments(building),
            }).pipe(
                map(({accounts}) => ({
                    periodId: building.periodId,
                    selectedAccount: {
                        building: building,
                        account: account,
                    },
                    accounts,
                    attachmentsExistsOnAnAccount: accounts.some(
                        it => it.attachments.length > 0 || it.uploading,
                    ),
                })),
            ),
        ),
    );

    constructor(
        public reportingService: ReportingService,
        public reportConsumersUploadService: ReportConsumersUploadService,
        private translateService: TranslateService,
        private toastr: ToastrService,
        public attachmentsUtil: AttachmentsUtil,
        private modalService: NgbModal,
    ) {}
    approve(
        periodId: string,
        selectedAccount: SelectedAccount,
        account: ReportingAccountWithAttachments,
    ): void {
        if (account.attachments.length === 0) {
            this.toastr.error(
                this.translateService.translate(
                    'reporting_FORM_INVALID_YOUR_INFORMATION_WAS_NOT_APPROVED_MISSING_ATTACHMENTS_TRY_AGAIN',
                ),
            );
            return;
        }

        const modalRef = this.modalService.open(ApproveUploadedConsumerDataModalComponent, {
            size: 'lg',
            backdrop: 'static',
        });
        const instance = modalRef.componentInstance as ApproveUploadedConsumerDataModalComponent;
        instance.periodId = periodId;
        instance.selectedAccount = selectedAccount;
    }

    uploadAttachment(
        event: Event,
        buildingNo: string,
        periodId: string,
        account: ReportingAccountWithAttachments,
    ): void {
        if (event.target === null) {
            return;
        }
        const target = event.target as HTMLInputElement;
        const files = target.files as FileList;
        const error = this.attachmentsUtil.checkUploadSizeLimit(files, account.attachments);
        if (error.singleFileSizeLimitExceeded) {
            this.toastr.error(this.translateService.translate('reporting_FILE_SIZE_LIMIT_20_MB'));
        } else if (error.totalFileSizeLimitExceeded) {
            this.toastr.error(
                this.translateService.translate('reporting_TOTAL_FILE_LIST_SIZE_100_MB'),
            );
        } else {
            combineLatest([
                ...this.attachmentsUtil.prepareAttachmentForUpload(files, file =>
                    this.reportConsumersUploadService.createConsumerDocAttachment(
                        buildingNo,
                        periodId,
                        account.accountPeriodId,
                        file,
                    ),
                ),
            ])
                .pipe(take(1))
                .subscribe();
        }
    }

    deleteAttachment(
        attachment: AttachmentFile,
        buildingNo: string,
        periodId: string,
        account: ReportingAccountWithAttachments,
    ): void {
        this.attachmentsUtil.deleteAttachment(attachment, () => {
            this.reportConsumersUploadService
                .deleteConsumerDocAttachments(
                    buildingNo,
                    periodId,
                    account.accountPeriodId,
                    attachment.attachmentId,
                )
                .subscribe();
        });
    }
}
